import React, { useCallback, useEffect, useState } from "react";
import {
    Container, Divider, Grid, Typography, Paper, TextField, Button, CircularProgress,
    IconButton, Dialog, AppBar, Toolbar, MenuItem, FormControl, Select, InputLabel,
    InputAdornment, Chip, Box, OutlinedInput,
} from "@mui/material";
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import Slide from '@mui/material/Slide';
import axios from 'axios';
import { useNavigate, useParams } from "react-router-dom";
import globals from "../globals";
import RTE from "../components/course_edit/rich_text_editor";
import FileUpload from "../components/course_edit/button_upload_file";
import { useAuth } from "../context/AuthContext";
import WebinarPage from "../events_page/webinars/webinar";

const PreviewTransition = React.forwardRef(function PreviewTransition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// The rich-text sections, in display order. Some carry an inline image upload.
const RICH_SECTIONS = [
    { id: 'what_is', label: 'Какво представлява уебинарът?' },
    { id: 'what_learn', label: 'Какво ще научиш?', imageField: 'what_learn_image' },
    { id: 'suitable_for', label: 'За кого е подходящ?' },
    { id: 'lecturer', label: 'Кой е лекторът? (ако е човек извън eAcademiaBg)', imageField: 'lecturer_image' },
    { id: 'how_conducted', label: 'Как ще се проведе?' },
    { id: 'what_get', label: 'Какво ще получиш?' },
    { id: 'program', label: 'Програма:' },
    { id: 'how_to_enroll', label: 'Как да се запишеш?', imageField: 'how_to_enroll_image' },
];

const emptyForm = {
    slug: '', title: '', price: '',
    dateValue: '', timeValue: '', instructor_ids: [], lecturer_name: '',
    what_is: '', what_learn: '', suitable_for: '', lecturer: '',
    how_conducted: '', what_get: '', program: '', how_to_enroll: '',
    meta_title: '', meta_description: '',
};

// yyyy-mm-dd in local time (toISOString would shift the day for times near midnight).
const localDateValue = (date) => {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${mm}-${dd}`;
};

const SectionTitle = ({ children }) => (
    <Typography sx={{ color: globals.colors.darkBlue, fontSize: 20, fontWeight: 'bold', mb: 1 }}>{children}</Typography>
);

const EditWebinar = () => {
    const { webinarId } = useParams();
    const isEdit = Boolean(webinarId);
    const { token } = useAuth();
    const navigate = useNavigate();

    const [form, setForm] = useState(emptyForm);
    // Uploaded File objects keyed by field name (banner, cover, lecturer_image, ...)
    const [files, setFiles] = useState({});
    // Existing image URLs (edit mode) keyed by *_image_url column name.
    const [existingImages, setExistingImages] = useState({});
    const [instructors, setInstructors] = useState([]);
    const [loaded, setLoaded] = useState(!isEdit);
    const [saving, setSaving] = useState(false);
    const [savedMessage, setSavedMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const [isPublished, setIsPublished] = useState(false);

    useEffect(() => {
        axios.get(`${globals.apiUrl}/admin/instructors`, {
            headers: { Authorization: `Bearer ${token}` },
        }).then((res) => setInstructors(res.data || []))
            .catch((err) => console.error('Failed to fetch instructors', err));
    }, [token]);

    useEffect(() => {
        if (!isEdit) return;
        axios.get(`${globals.apiUrl}/webinars/${webinarId}/edit`, {
            headers: { Authorization: `Bearer ${token}` },
        }).then((res) => {
            const w = res.data || {};
            const dateObj = w.date ? new Date(w.date) : null;
            setForm({
                slug: w.slug || '', title: w.title || '', price: w.price ?? '',
                dateValue: dateObj ? localDateValue(dateObj) : '',
                timeValue: dateObj ? dateObj.toTimeString().slice(0, 5) : '',
                instructor_ids: (w.instructors || []).map((i) => i.id),
                lecturer_name: w.lecturer_name || '',
                what_is: w.what_is || '', what_learn: w.what_learn || '',
                suitable_for: w.suitable_for || '', lecturer: w.lecturer || '',
                how_conducted: w.how_conducted || '', what_get: w.what_get || '',
                program: w.program || '', how_to_enroll: w.how_to_enroll || '',
                meta_title: w.meta_title || '', meta_description: w.meta_description || '',
            });
            setExistingImages({
                banner_image_url: w.banner_image_url,
                cover_image_url: w.cover_image_url,
                lecturer_image_url: w.lecturer_image_url,
                what_learn_image_url: w.what_learn_image_url,
                how_to_enroll_image_url: w.how_to_enroll_image_url,
            });
            setIsPublished(Boolean(w.is_published));
            setLoaded(true);
        }).catch((err) => {
            console.error('Failed to load webinar', err);
            setErrorMessage('Неуспешно зареждане на уебинара.');
            setLoaded(true);
        });
    }, [isEdit, webinarId, token]);

    const handleChange = (event) => {
        setForm((prev) => ({ ...prev, [event.target.id]: event.target.value }));
    };

    const handleSelectField = (field) => (event) => {
        setForm((prev) => ({ ...prev, [field]: event.target.value }));
    };

    const handleFile = (field) => (selected) => {
        setFiles((prev) => ({ ...prev, [field]: (selected && selected[0]) || null }));
    };

    const buildData = (isPublished) => {
        const data = {
            slug: form.slug || null,
            title: form.title || null,
            instructor_ids: form.instructor_ids,
            lecturer_name: form.lecturer_name.trim() || null,
            price: form.price === '' ? null : Number(form.price),
            what_is: form.what_is || null,
            what_learn: form.what_learn || null,
            suitable_for: form.suitable_for || null,
            lecturer: form.lecturer || null,
            how_conducted: form.how_conducted || null,
            what_get: form.what_get || null,
            program: form.program || null,
            how_to_enroll: form.how_to_enroll || null,
            meta_title: form.meta_title || null,
            meta_description: form.meta_description || null,
            is_published: isPublished,
        };
        if (form.dateValue) {
            data.date = `${form.dateValue}T${form.timeValue || '00:00'}:00`;
        }
        return data;
    };

    const handleSubmit = (publish) => () => {
        setSaving(true);
        setSavedMessage('');
        setErrorMessage('');

        const formData = new FormData();
        formData.append('data', JSON.stringify(buildData(publish)));
        ['banner', 'cover', 'lecturer_image', 'what_learn_image', 'how_to_enroll_image'].forEach((field) => {
            if (files[field]) formData.append(field, files[field]);
        });

        const url = isEdit ? `${globals.apiUrl}/webinars/${webinarId}` : `${globals.apiUrl}/webinars/`;
        const method = isEdit ? 'put' : 'post';

        axios({
            method, url, data: formData,
            headers: { Authorization: `Bearer ${token}` },
        }).then((res) => {
            setSaving(false);
            setIsPublished(publish);
            setSavedMessage(publish ? 'Успешно публикувахте уебинара!' : 'Успешно запазихте черновата!');
            if (!isEdit && res.data?.id) {
                navigate(`/admin-edit-webinar/${res.data.id}`, { replace: true });
            }
        }).catch((err) => {
            console.error('Failed to save webinar', err);
            setSaving(false);
            setErrorMessage('Възникна грешка при запазването. Моля, проверете полетата.');
        });
    };

    // Build an in-memory webinar object (shaped like the API detail response) for preview.
    const buildPreviewWebinar = () => {
        const objectUrl = (field, existingKey) =>
            (files[field] ? URL.createObjectURL(files[field]) : existingImages[existingKey]) || null;
        const selectedInstructors = form.instructor_ids
            .map((id) => instructors.find((i) => i.id === id))
            .filter(Boolean)
            .map((i) => ({
                id: i.id,
                name: `${i.first_name} ${i.last_name}`,
                image_url: i.image_url,
                description: i.description,
                education: i.education,
            }));
        return {
            ...buildData(true),
            id: webinarId ? Number(webinarId) : 0,
            date: form.dateValue ? `${form.dateValue}T${form.timeValue || '00:00'}:00` : null,
            instructors: selectedInstructors,
            banner_image_url: objectUrl('banner', 'banner_image_url'),
            cover_image_url: objectUrl('cover', 'cover_image_url'),
            lecturer_image_url: objectUrl('lecturer_image', 'lecturer_image_url'),
            what_learn_image_url: objectUrl('what_learn_image', 'what_learn_image_url'),
            how_to_enroll_image_url: objectUrl('how_to_enroll_image', 'how_to_enroll_image_url'),
        };
    };

    if (!loaded) {
        return (
            <Container sx={{ textAlign: 'center', mt: 10 }}>
                <CircularProgress sx={{ color: globals.colors.lightRed }} />
            </Container>
        );
    }

    const imageUpload = (field, existingKey, helpText) => (
        <Paper elevation={2} sx={{ maxWidth: 320, mt: 1, p: 1 }}>
            <FileUpload
                helpText={helpText}
                id={`upload-${field}`}
                onChange={handleFile(field)}
                uploadedFiles={existingImages[existingKey] ? [existingImages[existingKey]] : []}
            />
        </Paper>
    );

    return (
        <Container sx={{ mb: 6 }}>
            <Grid container justifyContent={'space-between'} alignItems={'center'} sx={{ mt: 2, mb: 1 }}>
                <Grid item>
                    <Typography component="span" sx={{ color: globals.colors.lightRed, fontWeight: 'bold', fontSize: 32, mr: 2 }}>
                        {isEdit ? 'Редактиране на уебинар' : 'Създаване на уебинар'}
                    </Typography>
                    {isEdit && (
                        <Chip
                            label={isPublished ? 'Публикуван' : 'Чернова'}
                            color={isPublished ? 'success' : 'default'}
                            size="small"
                            sx={{ verticalAlign: 'super' }}
                        />
                    )}
                </Grid>
                <Grid item>
                    <Button
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => setShowPreview(true)}
                        sx={{ mr: 1, color: globals.colors.lightGreen, borderColor: globals.colors.lightGreen, textTransform: 'none' }}
                    >
                        Провери
                    </Button>
                    <Button
                        variant="contained"
                        disabled={saving}
                        onClick={handleSubmit(false)}
                        sx={{ backgroundColor: globals.colors.darkBlue, textTransform: 'none', '&:hover': { backgroundColor: globals.colors.lightBlue } }}
                    >
                        {isPublished ? 'Върни в чернова' : 'Запази като чернова'}
                    </Button>
                </Grid>
            </Grid>
            <Divider sx={{ mb: 3 }} />

            <TextField
                label="Параметър (напр. webinar-1)"
                fullWidth value={form.slug} id="slug" onChange={handleChange}
                sx={{ mb: 3 }}
            />

            <SectionTitle>Заглавие</SectionTitle>
            <TextField fullWidth value={form.title} id="title" onChange={handleChange} sx={{ mb: 3 }} />

            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                    <SectionTitle>Заглавно изображение</SectionTitle>
                    {imageUpload('banner', 'banner_image_url', 'Добави заглавно изображение')}
                </Grid>
                <Grid item xs={12} sm={6}>
                    <SectionTitle>Корица</SectionTitle>
                    {imageUpload('cover', 'cover_image_url', 'Добави корица')}
                </Grid>
            </Grid>

            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                    <SectionTitle>Дата</SectionTitle>
                    <TextField type="date" fullWidth value={form.dateValue} id="dateValue" onChange={handleChange} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <SectionTitle>Час</SectionTitle>
                    <TextField type="time" fullWidth value={form.timeValue} id="timeValue" onChange={handleChange} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <SectionTitle>Цена</SectionTitle>
                    <TextField
                        type="number" fullWidth value={form.price} id="price" onChange={handleChange}
                        InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                    <SectionTitle>Учители от eAcademiaBg</SectionTitle>
                    <FormControl fullWidth>
                        <InputLabel id="instructors-label">Учители</InputLabel>
                        <Select
                            labelId="instructors-label"
                            multiple
                            value={form.instructor_ids}
                            onChange={handleSelectField('instructor_ids')}
                            input={<OutlinedInput label="Учители" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((id) => {
                                        const i = instructors.find((x) => x.id === id);
                                        return (
                                            <Chip
                                                key={id}
                                                size="small"
                                                label={i ? `${i.first_name} ${i.last_name}` : id}
                                                // Stop the mousedown so clicking the x doesn't open the dropdown.
                                                onMouseDown={(e) => e.stopPropagation()}
                                                onDelete={() => setForm((prev) => ({
                                                    ...prev,
                                                    instructor_ids: prev.instructor_ids.filter((x) => x !== id),
                                                }))}
                                            />
                                        );
                                    })}
                                </Box>
                            )}
                        >
                            {instructors.map((i) => (
                                <MenuItem key={i.id} value={i.id}>{i.first_name} {i.last_name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <SectionTitle>Лектор извън eAcademiaBg</SectionTitle>
                    <TextField
                        fullWidth value={form.lecturer_name} id="lecturer_name" onChange={handleChange}
                        placeholder="Име на лектора"
                        helperText="Показва се до датата и часа в началото на страницата."
                    />
                </Grid>
            </Grid>

            {RICH_SECTIONS.map((section) => (
                <Grid key={section.id} sx={{ mb: 3 }}>
                    <Grid container justifyContent={'space-between'} alignItems={'center'}>
                        <SectionTitle>{section.label}</SectionTitle>
                        {section.imageField && imageUpload(
                            section.imageField,
                            `${section.imageField}_url`,
                            section.imageField === 'lecturer_image' ? 'Добави снимка на лектора' : 'Добави картинка',
                        )}
                    </Grid>
                    <RTE defaultValue={form[section.id]} onChange={handleChange} idName={section.id} />
                </Grid>
            ))}

            <SectionTitle>Meta Title</SectionTitle>
            <TextField fullWidth value={form.meta_title} id="meta_title" onChange={handleChange} sx={{ mb: 3 }} />

            <SectionTitle>Meta Description</SectionTitle>
            <TextField fullWidth multiline rows={3} value={form.meta_description} id="meta_description" onChange={handleChange} sx={{ mb: 3 }} />

            <Grid container justifyContent={'flex-end'} alignItems={'center'} spacing={2} sx={{ mt: 2 }}>
                {errorMessage && <Grid item><Typography sx={{ color: globals.colors.darkRed }}>{errorMessage}</Typography></Grid>}
                {savedMessage && <Grid item><Typography sx={{ color: globals.colors.gray }}>{savedMessage}</Typography></Grid>}
                <Grid item>
                    <Button
                        variant="contained" size="large" disabled={saving}
                        onClick={handleSubmit(true)}
                        sx={{ px: 6, backgroundColor: globals.colors.lightRed, textTransform: 'none', '&:hover': { backgroundColor: globals.colors.darkRed } }}
                    >
                        {saving ? <CircularProgress size={24} sx={{ color: 'white' }} /> : (isPublished ? 'Запази и публикувай' : 'Публикувай')}
                    </Button>
                </Grid>
            </Grid>

            <Dialog fullScreen open={showPreview} onClose={() => setShowPreview(false)} TransitionComponent={PreviewTransition}>
                <AppBar sx={{ position: 'sticky', backgroundColor: globals.colors.darkBlue }}>
                    <Toolbar>
                        <Typography sx={{ flex: 1, fontWeight: 'bold' }} variant="h6" component="div">
                            Преглед на уебинара (текущи промени, без запис)
                        </Typography>
                        <IconButton edge="end" color="inherit" onClick={() => setShowPreview(false)} aria-label="close">
                            <CloseIcon />
                        </IconButton>
                    </Toolbar>
                </AppBar>
                <Box sx={{ py: 3 }}>
                    {showPreview && <WebinarPage webinar={buildPreviewWebinar()} />}
                </Box>
            </Dialog>
        </Container>
    );
};

export default EditWebinar;
